/**
 * Shared chrome for static sample-Reveal pages (LBG, Revolut, …).
 *
 * Each /sample/<account> route is its own thin component that owns the
 * hardcoded copy and forwards it here. Layout is intentionally
 * self-contained: no calibrate_reveal, no RevealRenderer, no API calls.
 *
 * Visual tokens mirror RevealRenderer.js so a subscriber who toggles
 * between a sample and a real Reveal feels continuity, not a design
 * switch. If RevealRenderer's tokens change, mirror the change here.
 *
 * Usage (see SampleReveal.jsx, SampleRevealRevolut.jsx):
 *
 *   <SampleRevealLayout
 *     testIdPrefix="sample-lbg"
 *     accountName="Lloyds Banking Group"
 *     weekLabel="Week of 4–10 May 2026"
 *     stalenessNotice="Example Reveal from W/C 4 May 2026. Subscribers see…"
 *     calibrationLensValue="Advisory / Win new business / Individual Contributor"
 *     signalThisWeekBody={<>Lloyds Banking Group appears to be…</>}
 *     nudge1Body={<>You're reading a sample Reveal…</>}
 *     conversionSampleTag="W/C 4 May 2026"
 *     actions={[
 *       { rank: 1, headline: "…", sourceLine: "…", paragraphs: [...], nudge: true },
 *       …
 *       { rank: 5, headline: "…", sourceLine: "…", paragraphs: [...], fade: true },
 *     ]}
 *   />
 */
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BetaBadge } from "@/components/BetaBadge";
import { KalibLogo } from "@/components/KalibLogo";

const SECTION_LABEL = {
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "0.08em",
  color: "#111111",
};

// CSS mask-image fade applied ONLY to the body container of fade cards.
// Headline + source line stay visible; the fade is the "subscribe to read
// more" mechanic — no toggle, no "Show more". DOM keeps the full prose so
// the bottom reads as inaccessible content, not absent content.
const FADE_BODY_STYLE = {
  WebkitMaskImage:
    "linear-gradient(to bottom, black 0%, black 55%, transparent 85%)",
  maskImage:
    "linear-gradient(to bottom, black 0%, black 55%, transparent 85%)",
  overflow: "hidden",
};

// All conversion paths land at /signup. Subscription opens 1 June 2026 —
// sign-up captures the seat in advance.
const SIGNUP_HREF = "/signup";

function StaticActionCard({ action, testIdPrefix }) {
  const { rank, headline, sourceLine, paragraphs, nudge, fade } = action;
  return (
    <div
      className="border-l-4 border-l-amber-700 border border-zinc-200 bg-white p-5"
      data-testid={`${testIdPrefix}-action-card-${rank}`}
    >
      <div className="flex items-start gap-4">
        <span
          className="flex-shrink-0 inline-flex items-center justify-center rounded-full bg-zinc-950 text-white font-semibold mt-0.5"
          style={{ width: "32px", height: "32px", fontSize: "15px", lineHeight: 1, letterSpacing: "-0.01em" }}
          aria-label={`Action ${rank}`}
        >
          {rank}
        </span>
        <div className="flex-1 min-w-0">
          <p style={{ fontSize: "17px", fontWeight: 600, color: "#111111", lineHeight: 1.4 }}>
            {headline}
          </p>
          <p
            className="mt-1"
            style={{ fontSize: "12px", fontWeight: 400, color: "#6f675c" }}
            data-testid={`${testIdPrefix}-action-source-${rank}`}
          >
            {sourceLine}
          </p>
          <div
            className="mt-3 text-zinc-900"
            style={{ fontSize: "15px", lineHeight: 1.65, ...(fade ? FADE_BODY_STYLE : {}) }}
            data-testid={`${testIdPrefix}-action-body-${rank}`}
          >
            {paragraphs.map((p, i) => (
              <p key={i} className="mb-3 last:mb-0">
                {p}
              </p>
            ))}
          </div>
          {nudge && (
            <div className="mt-4 pt-3 border-t border-zinc-100 flex justify-end">
              <Link
                to={SIGNUP_HREF}
                className="inline-flex items-center gap-1 text-[13px] italic text-amber-700 hover:text-amber-800 transition-colors"
                data-testid={`${testIdPrefix}-action-nudge-${rank}`}
              >
                Sign up to calibrate your own Reveal
                <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.75} />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SampleRevealLayout({
  testIdPrefix,
  accountName,
  weekLabel,
  stalenessNotice,
  calibrationLensValue,
  signalThisWeekBody,
  // Optional muted italic note beneath the Signal-This-Week body. Used
  // for thin-signal weeks (mirrors the "Lighter public-signal week than
  // usual …" treatment in RevealRenderer.js's actions counter).
  signalThinWeekNote,
  nudge1Body,
  conversionSampleTag,
  // Override the conversion-block heading. Default suits the standard
  // 5-card sample ("Three more moves…"). DB-style 4-card samples pass
  // "Two more moves on this account. Fresh every Monday." instead.
  conversionHeading = "Three more moves on this account. Fresh every Monday.",
  actions,
}) {
  return (
    <div className="min-h-screen bg-zinc-100" data-testid={`${testIdPrefix}-page`}>
      {/* Header bar */}
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Link
              to="/"
              className="block"
              data-testid={`${testIdPrefix}-logo-link`}
              aria-label="Kalib AI home"
            >
              <KalibLogo variant="light" height={28} testId={`${testIdPrefix}-header-logo`} />
            </Link>
            <BetaBadge />
          </div>
          <div className="flex items-center gap-4">
            <Link to={SIGNUP_HREF} data-testid={`${testIdPrefix}-header-cta`}>
              <Button className="bg-amber-700 text-white hover:bg-amber-800 rounded-none text-sm font-medium px-4 py-2">
                Sign Up
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Reveal header */}
        <div className="bg-white border border-zinc-200 p-6" data-testid={`${testIdPrefix}-reveal-header`}>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <p
                className="uppercase mb-1.5"
                style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.12em", color: "#6f675c" }}
              >
                Account
              </p>
              <h1
                className="font-heading mb-1"
                style={{ fontSize: "26px", fontWeight: 600, color: "#111111", lineHeight: 1.2 }}
                data-testid={`${testIdPrefix}-account-name`}
              >
                {accountName}
              </h1>
              <p style={{ fontSize: "13px", color: "#3a342e" }} data-testid={`${testIdPrefix}-week-label`}>
                {weekLabel}
              </p>
              <p
                className="mt-1.5 italic"
                style={{ fontSize: "12px", color: "#6f675c" }}
                data-testid={`${testIdPrefix}-staleness`}
              >
                {stalenessNotice}
              </p>
            </div>
            <span
              className="inline-flex items-center px-3 py-1 uppercase"
              style={{
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.12em",
                backgroundColor: "#fdf3df",
                color: "#92400E",
                border: "1px solid #d69b22",
              }}
              data-testid={`${testIdPrefix}-sample-pill`}
            >
              Sample
            </span>
          </div>

          {/* Calibration Lens — promoted to a SIGNAL-THIS-WEEK-style eyebrow
              row. Whole line shares the uppercase eyebrow font; both label
              and value render in amber-bold so the calibration handle
              reads as primary, not chrome. */}
          <p
            className="mt-4 pt-4 border-t border-zinc-100 uppercase"
            style={{ ...SECTION_LABEL, color: "#B45309" }}
            data-testid={`${testIdPrefix}-lens-row`}
          >
            <span>Calibration Lens:</span> <span>{calibrationLensValue}</span>
          </p>
        </div>

        {/* Signal This Week */}
        <div
          className="bg-white border border-zinc-200 border-l-4 border-l-amber-700 p-6"
          data-testid={`${testIdPrefix}-signal-this-week`}
        >
          <p className="uppercase mb-3" style={SECTION_LABEL}>
            Signal this week
          </p>
          <p style={{ fontSize: "15px", lineHeight: 1.65, color: "#1a1a1a" }}>
            {signalThisWeekBody}
          </p>
          {signalThinWeekNote && (
            <p
              className="mt-3 italic"
              style={{ fontSize: "13px", lineHeight: 1.55, color: "#6f675c" }}
              data-testid={`${testIdPrefix}-signal-thin-week-note`}
            >
              {signalThinWeekNote}
            </p>
          )}
        </div>

        {/* Nudge 1 — slim amber banner */}
        <div
          className="px-5 py-3"
          style={{ backgroundColor: "#fdf3df", border: "1px solid #d69b22", color: "#24211d" }}
          data-testid={`${testIdPrefix}-nudge-1`}
        >
          <p style={{ fontSize: "14px", fontWeight: 500, lineHeight: 1.55 }}>
            {nudge1Body}{" "}
            <Link
              to={SIGNUP_HREF}
              className="font-semibold underline-offset-2 hover:underline"
              style={{ color: "#92400E" }}
              data-testid={`${testIdPrefix}-nudge-1-link`}
            >
              → Sign up
            </Link>
          </p>
        </div>

        {/* Action cards */}
        {actions.map((action) => (
          <StaticActionCard key={action.rank} action={action} testIdPrefix={testIdPrefix} />
        ))}
      </div>

      {/* Conversion block — overlaps card 5's fade by ~40px so the
          dark-band transition reads as intentional, not gappy. */}
      <section
        className="mt-[-40px] relative z-10"
        style={{ backgroundColor: "#1c1916", color: "#f6f0e6" }}
        data-testid={`${testIdPrefix}-conversion-block`}
      >
        <div className="max-w-3xl mx-auto px-6 py-16 sm:py-20">
          <h2
            className="font-heading mb-4"
            style={{ fontSize: "32px", fontWeight: 600, lineHeight: 1.2, letterSpacing: "-0.01em" }}
          >
            {conversionHeading}
          </h2>
          <p className="mb-2" style={{ fontSize: "15px", lineHeight: 1.65, color: "#d6cfc1" }}>
            This is a sample Reveal from {conversionSampleTag} — one account, one lens, one
            week. Subscribers see a full Reveal every Monday, shaped to their firm
            type, motion, and seniority level. No firm name. No card required.
          </p>
          <p className="mb-8" style={{ fontSize: "13px", color: "#9c9486" }}>
            Subscription opens 1 June 2026.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to={SIGNUP_HREF} data-testid={`${testIdPrefix}-conversion-primary-cta`}>
              <Button
                className="rounded-none text-[14px] font-medium px-6 py-3 text-white w-full sm:w-auto inline-flex items-center gap-2"
                style={{ backgroundColor: "#d69b22" }}
              >
                Sign Up <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
